import React, { useState, useCallback } from "react";
import {
  Divider,
  FormControl,
  FormLabel,
  FormGroup,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  IconButton,
} from "@mui/material";
import { InfoButton, Button, Alert } from "../../../components";
import DbList from "./DbList";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hook";
import {
  setUserDbId,
  setUserLocationUsage,
  setUserObjectUsage,
  setUserLotUsage,
  setUserShowOnlyNotCollected,
  setUserExpandRows,
  setUserExtraFieldsUsage,
  setUserCameraUsage,
  setUserSearchUsage,
  setUserScanMode,
  setUserObject2Usage,
  setUserLot2Usage,
  setUserExpDateUsage,
} from "../../auth/slices/userSlice";
import { useSettingsStyles } from "../css";
import { userId, userChangeVersion } from "../../auth/selectors/userSelector";
import { useNavigate } from "react-router-dom";
import * as routes from "../../../app/routes";
import { isAlreadySet, otherUsersSettings } from "./utils";
import DepartmentList from "./DepartmentList";
import { useColorScheme } from "@mui/material/styles";
import { CloseOutlined } from "@mui/icons-material";
import UserInfo from "./UserInfo";

function Settings() {
  const { classes } = useSettingsStyles();
  const user = useAppSelector(userId);
  const [translate] = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { mode, setMode } = useColorScheme();
  const [localStorage] = useLocalStorage("userDb");
  const [scanModeLS, setScanModeLS] = useLocalStorage("scanMode", [
    { userId: user, mode: "default" },
  ]);
  const [objectLS, setObjectLS] = useLocalStorage("useObject", [
    { userId: user, isUsed: false },
  ]);
  const [locationLS, setLocationLS] = useLocalStorage("useLocation", [
    { userId: user, isUsed: false },
  ]);
  const [lotLS, setLotLS] = useLocalStorage("useLot", [
    { userId: user, isUsed: false },
  ]);
  const [object2LS, setObject2LS] = useLocalStorage("useObject2", [
    { userId: user, isUsed: false },
  ]);
  const [lot2LS, setLot2LS] = useLocalStorage("useLot2", [
    { userId: user, isUsed: false },
  ]);
  const [expDateLS, setExpDateLS] = useLocalStorage("useExpDate", [
    { userId: user, isUsed: false },
  ]);
  const [extraLS, setExtraLS] = useLocalStorage("useExtraFields", [
    { userId: user, isUsed: false },
  ]);
  const [showNotCollectedLS, setshowNotCollectedLS] = useLocalStorage(
    "showOnlyNotCollected",
    [{ userId: user, show: false }],
  );
  const [expandRowsLS, setExpandRowsLS] = useLocalStorage("expandRows", [
    { userId: user, allow: false },
  ]);
  const [useCameraLS, setUseCameraLS] = useLocalStorage("useCamera", [
    { userId: user, isUsed: false },
  ]);
  const [useSearchLs, setUseSearchLs] = useLocalStorage("useSearch", [
    { userId: user, isUsed: false },
  ]);
  const [checkCamera, setCheckCamera] = useState(false);
  const [checkLocation, setCheckLocation] = useState(false);
  const [checkObject, setCheckObject] = useState(false);
  const [checkLot, setCheckLot] = useState(false);
  const [checkObject2, setCheckObject2] = useState(false);
  const [checkLot2, setCheckLot2] = useState(false);
  const [checkExpDate, setCheckExpDate] = useState(false);
  const [checkExtra, setCheckExtra] = useState(false);
  const [checkNotCollected, setCheckNotCollected] = useState(false);
  const [checkExpandRows, setCheckExpandRows] = useState(false);
  const [checkSearch, setCheckSearch] = useState(false);
  const [scanMode, setScanMode] = useState("default");
  const newVersionButton = useAppSelector(userChangeVersion);
  const [openAlert, setOpenAlert] = useState(false);
  const currentMode = mode ? mode : window.localStorage.getItem("mui-mode");
  const darkMode = currentMode === "dark";

  React.useEffect(() => {
    //ScanMode
    const mode = isAlreadySet(scanModeLS, user);

    dispatch(setUserScanMode(mode.length > 0 ? mode[0].mode : "default"));

    if (mode.length > 0) setScanMode(mode[0].mode);
  }, [dispatch, user]);

  React.useEffect(() => {
    //User Db setting
    const isUser = isAlreadySet(localStorage, user);

    if (isUser.length > 0) {
      dispatch(setUserDbId(isUser[0]));
    }
  }, [dispatch, localStorage]);

  React.useEffect(() => {
    //useCamera setting
    const useCamera = isAlreadySet(useCameraLS, user);
    dispatch(
      setUserCameraUsage(useCamera.length > 0 ? useCamera[0].isUsed : false),
    );
    if (useCamera.length > 0) setCheckCamera(useCamera[0].isUsed);
  }, [dispatch, useCameraLS, user]);

  React.useEffect(() => {
    //Location setting
    const isLocation = isAlreadySet(locationLS, user);
    dispatch(
      setUserLocationUsage(
        isLocation.length > 0 ? isLocation[0].isUsed : false,
      ),
    );
    if (isLocation.length > 0) setCheckLocation(isLocation[0].isUsed);
  }, [dispatch, locationLS, user]);

  React.useEffect(() => {
    //Object setting
    const isObject = isAlreadySet(objectLS, user);

    dispatch(
      setUserObjectUsage(isObject.length > 0 ? isObject[0].isUsed : false),
    );
    if (isObject.length > 0) setCheckObject(isObject[0].isUsed);
  }, [dispatch, objectLS, user]);

  React.useEffect(() => {
    //Lot setting
    const isLot = isAlreadySet(lotLS, user);

    dispatch(setUserLotUsage(isLot.length > 0 ? isLot[0].isUsed : false));
    if (isLot.length > 0) setCheckLot(isLot[0].isUsed);
  }, [dispatch, lotLS, user]);

  React.useEffect(() => {
    //Object setting
    const isObject2 = isAlreadySet(object2LS, user);

    dispatch(
      setUserObject2Usage(isObject2.length > 0 ? isObject2[0].isUsed : false),
    );
    if (isObject2.length > 0) setCheckObject2(isObject2[0].isUsed);
  }, [dispatch, object2LS, user]);

  React.useEffect(() => {
    //Lot setting
    const isLot2 = isAlreadySet(lot2LS, user);

    dispatch(setUserLot2Usage(isLot2.length > 0 ? isLot2[0].isUsed : false));
    if (isLot2.length > 0) setCheckLot2(isLot2[0].isUsed);
  }, [dispatch, lot2LS, user]);

  React.useEffect(() => {
    //Lot setting
    const isExpDate = isAlreadySet(expDateLS, user);

    dispatch(
      setUserExpDateUsage(isExpDate.length > 0 ? isExpDate[0].isUsed : false),
    );
    if (isExpDate.length > 0) setCheckExpDate(isExpDate[0].isUsed);
  }, [dispatch, expDateLS, user]);

  React.useEffect(() => {
    //extraFields setting
    const isExtra = isAlreadySet(extraLS, user);

    dispatch(
      setUserExtraFieldsUsage(isExtra.length > 0 ? isExtra[0].isUsed : false),
    );
    if (isExtra.length > 0) setCheckExtra(isExtra[0].isUsed);
  }, [dispatch, extraLS, user]);

  React.useEffect(() => {
    //showNotCollected setting
    const showNotCollected = isAlreadySet(showNotCollectedLS, user);

    dispatch(
      setUserShowOnlyNotCollected(
        showNotCollected.length > 0 ? showNotCollected[0].show : false,
      ),
    );

    if (showNotCollected.length > 0)
      setCheckNotCollected(showNotCollected[0].show);
  }, [dispatch, showNotCollectedLS, user]);

  React.useEffect(
    () => {
      //ExpandRows setting
      const expandRows = isAlreadySet(expandRowsLS, user);

      dispatch(
        setUserExpandRows(expandRows.length > 0 ? expandRows[0].allow : false),
      );

      if (expandRows.length > 0) setCheckExpandRows(expandRows[0].allow);
    }, // eslint-disable-next-line
    [dispatch, expandRowsLS, user],
  );

  React.useEffect(
    () => {
      //Search usage setting
      const search = isAlreadySet(useSearchLs, user);

      dispatch(
        setUserSearchUsage(search.length > 0 ? search[0].isUsed : false),
      );

      if (search.length > 0) setCheckSearch(search[0].isUsed);
    }, // eslint-disable-next-line
    [dispatch, useSearchLs, user],
  );

  const handleScanMode = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    if (value === "default" || value === "autoMode") {
      dispatch(setUserScanMode(value));
      setScanMode(value);
      const otherUsers = otherUsersSettings(scanModeLS, user);
      setScanModeLS([...otherUsers, { userId: user, mode: value }]);
    }
  };

  const handleCamera = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserCameraUsage(event.target.checked));
    const otherUsers = otherUsersSettings(useCameraLS, user);
    setUseCameraLS([
      ...otherUsers,
      { userId: user, isUsed: event.target.checked },
    ]);
  };

  const handleLocation = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserLocationUsage(event.target.checked));
    const otherUsers = otherUsersSettings(locationLS, user);
    setLocationLS([
      ...otherUsers,
      { userId: user, isUsed: event.target.checked },
    ]);
  };

  const handleObject = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserObjectUsage(event.target.checked));
    const otherUsers = otherUsersSettings(objectLS, user);
    setObjectLS([
      ...otherUsers,
      { userId: user, isUsed: event.target.checked },
    ]);
  };

  const handleLot = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserLotUsage(event.target.checked));
    const otherUsers = otherUsersSettings(lotLS, user);
    setLotLS([...otherUsers, { userId: user, isUsed: event.target.checked }]);
  };

  const handleObject2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserObject2Usage(event.target.checked));
    const otherUsers = otherUsersSettings(object2LS, user);
    setObject2LS([
      ...otherUsers,
      { userId: user, isUsed: event.target.checked },
    ]);
  };

  const handleLot2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserLot2Usage(event.target.checked));
    const otherUsers = otherUsersSettings(lot2LS, user);
    setLot2LS([...otherUsers, { userId: user, isUsed: event.target.checked }]);
  };

  const handleExpDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserExpDateUsage(event.target.checked));
    const otherUsers = otherUsersSettings(expDateLS, user);
    setExpDateLS([
      ...otherUsers,
      { userId: user, isUsed: event.target.checked },
    ]);
  };

  const handleExtra = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserExtraFieldsUsage(event.target.checked));
    const otherUsers = otherUsersSettings(extraLS, user);
    setExtraLS([...otherUsers, { userId: user, isUsed: event.target.checked }]);
  };

  const handleShowNotCollected = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setUserShowOnlyNotCollected(event.target.checked));
    const otherUsers = otherUsersSettings(showNotCollectedLS, user);

    setshowNotCollectedLS([
      ...otherUsers,
      { userId: user, show: event.target.checked },
    ]);
  };

  const handleExpand = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserExpandRows(event.target.checked));
    const otherUsers = otherUsersSettings(expandRowsLS, user);
    setExpandRowsLS([
      ...otherUsers,
      { userId: user, allow: event.target.checked },
    ]);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserSearchUsage(event.target.checked));
    const otherUsers = otherUsersSettings(useSearchLs, user);
    setUseSearchLs([
      ...otherUsers,
      { userId: user, isUsed: event.target.checked },
    ]);
  };

  const handleBack = () => {
    navigate(routes.ROUTE_OPTIONS.path, {
      state: { from: routes.ROUTE_SETTINGS.path },
    });
  };

  const handleMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setMode("dark");
    } else setMode("light");
  };

  const changeVersion = useCallback(() => {
    window.localStorage.removeItem("mscanVersija");
    const currentPath = location.pathname;
    const newPath = currentPath.includes("/v3")
      ? currentPath.replace("/v3", "")
      : currentPath;

    navigate(newPath, { replace: true });
    window.location.reload();
    setOpenAlert(false);
  }, [location]);

  return (
    <div>
      {openAlert && (
        <Alert
          isOpen={openAlert}
          onClose={() => setOpenAlert(false)}
          message={translate("confirmVersionChange")}
          isConfirmationButton
          onConfirm={changeVersion}
          context="versionChange"
        />
      )}

      <div className={classes.top}>
        <Typography classes={{ root: classes.moduleName }}>
          {translate("settings", "Nustatymai")}
        </Typography>

        <IconButton
          edge="end"
          color="inherit"
          size="medium"
          onClick={handleBack}
        >
          <CloseOutlined />
        </IconButton>
      </div>

      <div className={classes.dbListWrapper}>
        <UserInfo />
        <div className={classes.dbList}>
          <DbList />
        </div>
      </div>
      <DepartmentList />
      <Divider variant="fullWidth" className={classes.divider} />

      <div className={classes.root}>
        <div className={classes.container}>
          <FormControl focused={false}>
            <FormLabel component="legend" className={classes.groupLegend}>
              {translate("scanningModes", "Skenavimo rėžimai:")}
            </FormLabel>

            <RadioGroup
              className={classes.settingsGroup}
              value={scanMode}
              onChange={handleScanMode}
            >
              <FormControlLabel
                value="default"
                control={<Radio />}
                label={
                  <div className={classes.infoButton}>
                    {translate("default", "Numatytas")}
                    <InfoButton
                      text={translate(
                        "defaultModeInfo",
                        'Nuskenuoti duomenys fiksuojami paspaudus "Tęsti" mygtuką.',
                      )}
                    />
                  </div>
                }
              />
              <FormControlLabel
                value="autoMode"
                control={<Radio />}
                label={
                  <div className={classes.infoButton}>
                    {translate("autoMode", "Automatinis")}
                    <InfoButton
                      text={translate(
                        "autoModeInfo",
                        'Nuskenuoti duomenys fiksuojami <ENTER> klavišo paspaudimu esant bet kuriame lauke. Mygtukas "Tęsti" nerodomas.',
                      )}
                    />
                  </div>
                }
              />
            </RadioGroup>

            <Divider variant="fullWidth" className={classes.divider} />
          </FormControl>

          <FormControl focused={false}>
            <FormLabel component="legend" className={classes.groupLegend}>
              {translate("showAdditionalFields", "Papildomų laukų rodymas:")}
            </FormLabel>
            <FormGroup className={classes.settingsGroup}>
              <FormControlLabel
                control={
                  <Switch
                    onChange={handleLocation}
                    checked={checkLocation}
                    value={checkLocation}
                  />
                }
                label={translate("location", "Lokacija")}
              />
              <FormControlLabel
                control={
                  <Switch
                    onChange={handleObject}
                    checked={checkObject}
                    value={checkObject}
                  />
                }
                label={translate("object", "Objektas")}
              />

              <FormControlLabel
                control={
                  <Switch
                    onChange={handleObject2}
                    checked={checkObject2}
                    value={checkObject2}
                  />
                }
                label={translate("object2", "Gavėjo objektas")}
              />

              <FormControlLabel
                control={
                  <Switch
                    onChange={handleLot}
                    checked={checkLot}
                    value={checkLot}
                  />
                }
                label={translate("lot", "Serija")}
              />

              <FormControlLabel
                control={
                  <Switch
                    onChange={handleLot2}
                    checked={checkLot2}
                    value={checkLot2}
                  />
                }
                label={translate("lot2", "Gavėjo serija")}
              />

              <FormControlLabel
                control={
                  <Switch
                    onChange={handleExpDate}
                    checked={checkExpDate}
                    value={checkExpDate}
                  />
                }
                label={translate("expDate", "Galiojimo data")}
              />

              <FormControlLabel
                control={
                  <Switch
                    onChange={handleExtra}
                    checked={checkExtra}
                    value={checkExtra}
                  />
                }
                label={translate("extraFields", "Papildoma informacija")}
              />

              <FormControlLabel
                control={
                  <Switch
                    onChange={handleSearch}
                    checked={checkSearch}
                    value={checkSearch}
                  />
                }
                label={translate("useSearchField", "Paieškos laukas dokumente")}
              />

              <Divider variant="fullWidth" className={classes.divider} />
            </FormGroup>
          </FormControl>

          <FormControl focused={false}>
            <FormLabel component="legend" className={classes.groupLegend}>
              {translate("device", "Įrenginys:")}
            </FormLabel>
            <FormGroup className={classes.settingsGroup}>
              <FormControlLabel
                control={
                  <Switch
                    onChange={handleCamera}
                    checked={checkCamera}
                    value={checkCamera}
                  />
                }
                label={translate("turnOnCamera", "Naudoti įrenginio kamera")}
              />
            </FormGroup>
            <Divider variant="fullWidth" className={classes.divider} />
          </FormControl>

          <FormControl focused={false}>
            <FormLabel component="legend" className={classes.groupLegend}>
              {translate("otherSettings", "Kiti nustatymai:")}
            </FormLabel>
            <FormGroup className={classes.settingsGroup}>
              <FormControlLabel
                control={
                  <Switch
                    onChange={handleShowNotCollected}
                    checked={checkNotCollected}
                    value={checkNotCollected}
                  />
                }
                label={translate(
                  "showOnlyNotFullyCollected",
                  "Rodyti tik neatriktas prekes",
                )}
              />

              <FormControlLabel
                control={
                  <Switch
                    onChange={handleExpand}
                    checked={checkExpandRows}
                    value={checkExpandRows}
                  />
                }
                label={translate(
                  "allowRowHeightChanges",
                  "Praplėsti eilutę priklausomai nuo turinio",
                )}
              />
            </FormGroup>

            <Divider variant="fullWidth" className={classes.divider} />
          </FormControl>

          <FormControl focused={false}>
            <FormLabel component="legend" className={classes.groupLegend}>
              {translate("programMode")}
            </FormLabel>
            <FormGroup className={classes.settingsGroup}></FormGroup>

            <FormControlLabel
              control={
                <Switch
                  onChange={handleMode}
                  checked={darkMode}
                  value={darkMode}
                />
              }
              label={translate("darkMode")}
            />

            {newVersionButton && (
              <Divider variant="fullWidth" className={classes.divider} />
            )}
          </FormControl>

          {newVersionButton && (
            <FormControl focused={false}>
              <FormLabel component="legend" className={classes.groupLegend}>
                {translate("versions")}
              </FormLabel>

              <Typography className={classes.chooseVersion}>
                {translate("chooseBetween")}
              </Typography>

              <div className={classes.versionBtn}>
                <Button onClick={() => setOpenAlert(true)} fullWidth>
                  {translate("changeVersion")}
                </Button>
              </div>
            </FormControl>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
